import { useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { usePageTitle } from '../hooks/usePageTitle'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { organizationsApi, type Organization } from '../api/contacts'
import { useAuthStore } from '../store/auth'
import { Pagination } from '../components/ui/Pagination'
import { Modal } from '../components/ui/Modal'
import { PageSpinner } from '../components/ui/Spinner'
import { EmptyState } from '../components/ui/EmptyState'
import { formatDate } from '../lib/utils'
import { Plus, Building2, Search, Pencil } from 'lucide-react'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'

export function OrganizationsListPage() {
  usePageTitle('Organizations')
  const { companyId } = useParams<{ companyId: string }>()
  const navigate = useNavigate()
  const qc = useQueryClient()
  const { user } = useAuthStore()
  const isOwner = user?.role === 'company_owner' || user?.role === 'administrator'
  const [page, setPage] = useState(1)
  const [showCreate, setShowCreate] = useState(false)
  const [editingOrg, setEditingOrg] = useState<Organization | null>(null)
  const [search, setSearch] = useState('')

  const { data, isLoading } = useQuery({
    queryKey: ['organizations', companyId, page, search],
    queryFn: () => organizationsApi.list(companyId!, { page, limit: 25, search: search || undefined }).then(r => r.data),
    enabled: !!companyId,
  })

  const onMutateSuccess = () => qc.invalidateQueries({ queryKey: ['organizations', companyId] })

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200 bg-white">
        <h1 className="text-base font-semibold text-gray-900">Organizations</h1>
        <div className="flex items-center gap-2">
          <div className="relative">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400 pointer-events-none" />
            <input
              value={search}
              onChange={e => { setSearch(e.target.value); setPage(1) }}
              placeholder="Search organizations..."
              className="input pl-8 h-8 text-sm w-48"
            />
          </div>
          {isOwner && (
            <button onClick={() => setShowCreate(true)} className="btn btn-primary btn-sm">
              <Plus className="w-3.5 h-3.5" /> New Organization
            </button>
          )}
        </div>
      </div>

      <div className="flex-1 overflow-auto bg-white">
        {isLoading ? <PageSpinner /> : (
          <table className="table">
            <thead>
              <tr><th>Name</th><th>Description</th><th>Status</th><th>Created</th>{isOwner && <th />}</tr>
            </thead>
            <tbody>
              {data?.data.length === 0 && (
                <tr><td colSpan={isOwner ? 5 : 4}>
                  <EmptyState icon={<Building2 className="w-10 h-10" />} title="No organizations" />
                </td></tr>
              )}
              {data?.data.map((o: Organization) => (
                <tr key={o.id} className="cursor-pointer" onClick={() => navigate(`/company/${companyId}/organizations/${o.id}`)}>
                  <td className="font-medium">
                    <div className="flex items-center gap-2">
                      {o.color && <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: o.color }} />}
                      {o.name}
                    </div>
                  </td>
                  <td className="text-gray-600">{o.description ?? '—'}</td>
                  <td>
                    <span className={`badge ${o.isActive ? 'badge-green' : 'badge-gray'}`}>
                      {o.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td className="text-gray-500 text-xs">{formatDate(o.createdAt)}</td>
                  {isOwner && (
                    <td onClick={e => e.stopPropagation()}>
                      <button
                        onClick={() => setEditingOrg(o)}
                        className="btn btn-ghost btn-sm p-1"
                        title="Edit"
                      >
                        <Pencil className="w-3.5 h-3.5" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {data && data.meta.totalPages > 1 && (
        <Pagination page={data.meta.page} totalPages={data.meta.totalPages} total={data.meta.total} limit={25} onPageChange={setPage} />
      )}

      {isOwner && (
        <>
          <CreateOrgModal
            open={showCreate}
            onClose={() => setShowCreate(false)}
            companyId={companyId!}
            onSuccess={() => { setShowCreate(false); onMutateSuccess() }}
          />
          {editingOrg && (
            <EditOrgModal
              org={editingOrg}
              open={!!editingOrg}
              onClose={() => setEditingOrg(null)}
              companyId={companyId!}
              onSuccess={() => { setEditingOrg(null); onMutateSuccess() }}
            />
          )}
        </>
      )}
    </div>
  )
}

const PRESET_COLORS = ['#a5b4fc', '#6ee7b7', '#fcd34d', '#f9a8d4', '#93c5fd', '#fdba74', '#86efac', '#c4b5fd']

function OrgForm({
  defaultValues,
  onSubmit,
  isPending,
  onClose,
  submitLabel,
}: {
  defaultValues: { name: string; description?: string; color?: string }
  onSubmit: (d: { name: string; description?: string; color?: string }) => void
  isPending: boolean
  onClose: () => void
  submitLabel: string
}) {
  const { register, handleSubmit, watch, setValue } = useForm({ defaultValues })
  const selectedColor = watch('color')
  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      <div><label className="label">Name *</label><input {...register('name', { required: true })} className="input" /></div>
      <div><label className="label">Description</label><input {...register('description')} className="input" /></div>
      <div>
        <label className="label">Color</label>
        <div className="flex items-center gap-2">
          {PRESET_COLORS.map(c => (
            <button
              key={c}
              type="button"
              onClick={() => setValue('color', c)}
              className="w-7 h-7 rounded-full border-2 transition-all"
              style={{ backgroundColor: c, borderColor: selectedColor === c ? '#1d4ed8' : 'transparent' }}
            />
          ))}
          <input
            type="color"
            value={selectedColor ?? PRESET_COLORS[0]}
            onChange={e => setValue('color', e.target.value)}
            className="w-7 h-7 rounded cursor-pointer border border-gray-300"
            title="Custom color"
          />
        </div>
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <button type="button" onClick={onClose} className="btn btn-secondary btn-sm">Cancel</button>
        <button type="submit" disabled={isPending} className="btn btn-primary btn-sm">{submitLabel}</button>
      </div>
    </form>
  )
}

function CreateOrgModal({ open, onClose, companyId, onSuccess }: {
  open: boolean; onClose: () => void; companyId: string; onSuccess: () => void
}) {
  const mutation = useMutation({
    mutationFn: (dto: { name: string; description?: string; color?: string }) =>
      organizationsApi.create(companyId, dto),
    onSuccess: () => { toast.success('Organization created'); onSuccess() },
  })
  return (
    <Modal open={open} onClose={onClose} title="New Organization" size="sm">
      <OrgForm
        defaultValues={{ name: '', color: PRESET_COLORS[0] }}
        onSubmit={d => mutation.mutate(d)}
        isPending={mutation.isPending}
        onClose={onClose}
        submitLabel="Create"
      />
    </Modal>
  )
}

function EditOrgModal({ org, open, onClose, companyId, onSuccess }: {
  org: Organization; open: boolean; onClose: () => void; companyId: string; onSuccess: () => void
}) {
  const mutation = useMutation({
    mutationFn: (dto: { name: string; description?: string; color?: string }) =>
      organizationsApi.update(companyId, org.id, dto),
    onSuccess: () => { toast.success('Organization updated'); onSuccess() },
  })
  return (
    <Modal open={open} onClose={onClose} title="Edit Organization" size="sm">
      <OrgForm
        defaultValues={{ name: org.name, description: org.description ?? '', color: org.color ?? PRESET_COLORS[0] }}
        onSubmit={d => mutation.mutate(d)}
        isPending={mutation.isPending}
        onClose={onClose}
        submitLabel="Save Changes"
      />
    </Modal>
  )
}
